import { useId, useState, type CSSProperties, type ReactNode } from "react";
import { X } from "lucide-react";
import { Combobox } from "./Combobox";
import { comparableText, normalizeComboboxValue } from "./text";

/**
 * Entrada de conjunto (tags e gatilhos) — um único controle compartilhado,
 * como exige a regra de tags e gatilhos usarem o mesmo componente
 * (AGENTS §14.4).
 *
 * Criar um item novo é ação explícita dentro da lista de sugestões, nunca
 * efeito de sair do campo ou de teclar espaço (§7.1). Cada item selecionado
 * vira um botão de remoção alcançável por teclado.
 */
export interface TokenOption {
  id: string;
  name: string;
  color?: string;
}

export interface TokenPickerProps {
  label: string;
  options: readonly TokenOption[];
  /** Identificadores selecionados, na ordem em que entraram. */
  value: readonly string[];
  onChange: (ids: string[]) => void;
  /** Cria um item novo e devolve o identificador; ausente desliga a criação. */
  onCreate?: (name: string) => Promise<string | undefined>;
  help?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  disabledReason?: string;
  className?: string;
}

export function TokenPicker({
  label,
  options,
  value,
  onChange,
  onCreate,
  help,
  placeholder = "Buscar ou criar",
  disabled = false,
  disabledReason,
  className,
}: TokenPickerProps) {
  const baseId = useId();
  const helpId = help ? `${baseId}-help` : undefined;
  const [busy, setBusy] = useState(false);

  const selected = value
    .map((id) => options.find((option) => option.id === id))
    .filter((option): option is TokenOption => Boolean(option));

  const available = options.filter((option) => !value.includes(option.id));

  const add = async (text: string) => {
    const name = normalizeComboboxValue(text);
    if (!name) return;
    const existing = options.find((option) => comparableText(option.name) === comparableText(name));
    if (existing) {
      if (!value.includes(existing.id)) onChange([...value, existing.id]);
      return;
    }
    if (!onCreate) return;
    setBusy(true);
    try {
      const id = await onCreate(name);
      if (id) onChange([...value, id]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className ? `cx-tokens ${className}` : "cx-tokens"}>
      <span className="cx-field-label" id={`${baseId}-label`}>
        {label}
      </span>
      {selected.length > 0 && (
        <ul className="cx-token-list" aria-label={`${label} selecionados`}>
          {selected.map((option) => (
            <li key={option.id}>
              <span className="cx-token" style={option.color ? ({ "--cx-token-tone": option.color } as CSSProperties) : undefined}>
                <span>{option.name}</span>
                <button
                  type="button"
                  aria-label={`Remover ${option.name}`}
                  onClick={() => onChange(value.filter((id) => id !== option.id))}
                  disabled={disabled}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <Combobox
        suggestions={available.map((option) => option.name)}
        value=""
        onChange={(text) => void add(text)}
        labelledBy={`${baseId}-label`}
        placeholder={placeholder}
        disabled={disabled || busy}
        disabledReason={disabledReason}
        describedBy={helpId}
        createLabel={(text) => (onCreate ? `Criar “${text}”` : `Usar “${text}”`)}
      />
      {help && (
        <p className="cx-field-help" id={helpId}>
          {help}
        </p>
      )}
    </div>
  );
}
